

import React, { useContext, useState, useEffect, useRef } from 'react';
import DataContext from '../../stores/DataContextProvider';
import { useNavigate } from 'react-router-dom';
import backendIP from '../../utils/serverData';
import Popup from '../popup/Popup';
import Goal from '../controllers/Goal';
import './TestScreen3.css';

const TestScreen3 = () => {
  const { sk, g, instruction, folderPath } = useContext(DataContext);
  const [displayScript, setDisplayScript] = useState('');
  const [selectedFileName, setSelectedFileName] = useState('');
  const [score, setScore] = useState(0);
  const [totalAudioPlayed, setTotalAudioPlayed] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [playedFiles, setPlayedFiles] = useState([]);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const audioRef = useRef(null);
  const abortControllerRef = useRef(new AbortController());
  const navigate = useNavigate();

  useEffect(() => {
    if (folderPath) {
      fetchSelectedAudio();
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [folderPath]);

  const fetchSelectedAudio = async () => {
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();
      const response = await fetch(`${backendIP}/audio/listfiles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folderPath }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const errorDetails = await response.text();
        throw new Error(`Failed to fetch audio file list: ${errorDetails}`);
      }

      const audioData = await response.json();

      if (Array.isArray(audioData) && audioData.length > 0) {
        let availableFiles = audioData.filter(file => !playedFiles.includes(file));

        if (availableFiles.length === 0) {
          availableFiles = audioData;
          setPlayedFiles([]);
        }

        const randomIndex = Math.floor(Math.random() * availableFiles.length);
        const selectedFileName = availableFiles[randomIndex];
        const fileNameWithoutExtension = selectedFileName.replace('.wav', '');

        setSelectedFileName(selectedFileName);
        setDisplayScript(fileNameWithoutExtension);
        setPlayedFiles(prevFiles => [...prevFiles, selectedFileName]);
        playAudio(selectedFileName);
      } else {
        console.warn('No audio files found in the folder');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching audio file:', error);
      }
    }
  };

  const playAudio = async (fileName) => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }

      const filenameWithPath = `${folderPath}/${fileName}`;

      const response = await fetch(`${backendIP}/audio/getaudio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filenameWithPath }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const errorDetails = await response.text();
        throw new Error(`Failed to fetch audio file: ${errorDetails}`);
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);

      if (!audioRef.current) {
        audioRef.current = new Audio();
      }

      audioRef.current.src = audioUrl;
      setIsAudioPlaying(true);

      audioRef.current.play();
      audioRef.current.onended = () => {
        setIsAudioPlaying(false); // Re-enable buttons when audio ends
      };
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Failed to load audio file:', error);
      }
    }
  };

  const handleCorrect = () => {
    if (!isAudioPlaying) {
      setScore(prevScore => prevScore + 1);
      setTotalAudioPlayed(prevTotal => prevTotal + 1);
      setTimeout(() => {
        fetchSelectedAudio();
      }, 3000);
    }
  };

  const handleIncorrect = () => {
    if (!isAudioPlaying) {
      setTotalAudioPlayed(prevTotal => prevTotal + 1);
      setTimeout(() => {
        fetchSelectedAudio();
      }, 3000);
    }
  };

  const handleRepeat = () => {
    if (selectedFileName) {
      playAudio(selectedFileName);
    }
  };

  const handleExit = () => {
    setShowPopup(true);

    // Stop audio playback
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = ''; // Clear the audio source
    }

    // Abort ongoing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Reset states to stop further actions
    setIsAudioPlaying(false);
    setSelectedFileName('');
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    navigate('/home'); // Navigate to home page
    console.log('Popup closed, navigating to home');
  };

  return (
    <div className="test-screen3">
      <Goal sk={sk} />
      <div className="content">
        <h4>{instruction}</h4>
        <div className="audio-icon" onClick={() => audioRef.current && audioRef.current.play()}>
          <span role="img" aria-label="speaker">🔊</span>
        </div>
        <h2 className="display-script">{displayScript}</h2>
      </div>
      <div className="button-row">
        <div className="button-row correct-incorrect">
          <button
            className="button correct"
            onClick={handleCorrect}
            disabled={isAudioPlaying || showPopup}
          >
            Correct
          </button>
          <button
            className="button incorrect"
            onClick={handleIncorrect}
            disabled={isAudioPlaying || showPopup}
          >
            Incorrect
          </button>
        </div>
        <div className="button-row repeat-exit">
          <button className="button repeat" onClick={handleRepeat}>
            Repeat
          </button>
          <button className="button exit" onClick={handleExit}>
            Exit
          </button>
        </div>
      </div>
      {showPopup && (
        <Popup score={score} totalAudioPlayed={totalAudioPlayed} onClose={handleClosePopup} />
      )}
    </div>
  );
};

export default TestScreen3;
